import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Key,  AlertCircle, Loader } from 'lucide-react';
import api from '../utils/axios';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { DevProject } from '../types/chat';
import { IfHasPermission } from './IfHasPermission';
import { Permissions } from '../constants';

// Custom Edit Icon SVG
const EditIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="15" 
    height="15" 
    viewBox="0 0 15 15" 
    fill="none"
    className={className}
  >
    <path 
      d="M0.953819 11.3294C0.984448 11.0537 0.999762 10.9159 1.04147 10.7871C1.07847 10.6728 1.13075 10.564 1.19688 10.4637C1.27143 10.3507 1.36949 10.2526 1.56561 10.0565L10.3698 1.2523C11.1062 0.515917 12.3001 0.515918 13.0365 1.2523C13.7729 1.98868 13.7729 3.18259 13.0365 3.91897L4.23228 12.7231C4.03616 12.9193 3.93809 13.0173 3.82505 13.0919C3.72476 13.158 3.61599 13.2103 3.50169 13.2473C3.37286 13.289 3.23503 13.3043 2.95937 13.3349L0.703125 13.5856L0.953819 11.3294Z" 
      stroke="currentColor" 
      strokeWidth="1.4" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// Pills mirror the neutral platform badge on the dashboard (see getPlatformBadgeClass
// in config/platformConfig.ts). The Veylo palette is chartreuse + neutrals, so state is
// carried by the border and fill rather than by a different hue per badge — the old
// blue / green / amber utility pills clashed with it.
const PILL_BASE =
  'border px-bg-v2-md py-bg-v2-xxs rounded-bg-v2-full text-bg-v2-xs ' +
  'font-bg-v2-medium tracking-wide whitespace-nowrap shrink-0';

const PILL_VARIANTS = {
  // Identical to the dashboard's Web / TAP / SN badge.
  neutral: 'bg-bg-primary border-border-primary text-text-primary',
  // Brand accent on the border only: bg-brand-primary flips to a bright chartreuse in
  // dark mode, where text-primary sitting on it would be unreadable.
  brand: 'bg-bg-primary border-border-brand text-text-primary',
  // Recedes, for an empty or inactive state.
  muted: 'bg-bg-secondary border-border-secondary text-text-tertiary',
} as const;

const Pill: React.FC<{
  variant?: keyof typeof PILL_VARIANTS;
  children: React.ReactNode;
}> = ({ variant = 'neutral', children }) => (
  <span className={`${PILL_BASE} ${PILL_VARIANTS[variant]}`}>{children}</span>
);

interface AuthCredentialsModalProps {
  project: DevProject | null;
  onClose: () => void;
}

interface CredentialParameter {
  id: string;
  key_name: string;
  display_name: string;
  value_type: 'secure' | 'plain_text';
  description: string;
  env_file_path: string;
  created_at: string;
  updated_at?: string;
  has_value: boolean;
  required?: boolean;
  value?: string; // Only populated for plain_text type
}

// Skeleton Loading Component for Credential Cards
const CredentialCardSkeleton: React.FC<{ index: number }> = ({ index }) => {
  return (
    <div 
      className="animate-fade-in-up opacity-0"
      style={{ 
        animationDelay: `${index * 0.08}s`,
        animationFillMode: 'forwards'
      }}
    >
      <div 
        className="bg-bg-primary border border-border-secondary box-border flex gap-[8px] items-start p-[var(--spacing-lg,12px)] rounded-[var(--radius-md,8px)] w-full"
        style={{
          boxShadow: "0px 1px 3px 0px var(--shadow-sm-01, rgba(10, 13, 18, 0.1)), 0px 1px 2px -1px var(--shadow-sm-02, rgba(10, 13, 18, 0.1))"
        }}
      >
        {/* Content Skeleton */}
        <div className="flex flex-col gap-[6px] items-start flex-1 min-w-0">
          {/* Key name and badges skeleton */}
          <div className="flex items-center gap-[11px] w-full">
            <div className="h-6 skeleton-shimmer rounded w-2/5"></div>
            <div className="h-5 skeleton-shimmer rounded w-16"></div>
            <div className="h-5 skeleton-shimmer rounded w-12"></div>
          </div>
          
          {/* Display name skeleton */}
          <div className="h-5 skeleton-shimmer rounded w-3/5"></div>
          
          {/* Description skeleton */}
          <div className="h-4 skeleton-shimmer rounded w-full"></div>
        </div>

        {/* Edit Button Skeleton */}
        <div className="w-[25px] h-[25px] skeleton-shimmer rounded shrink-0"></div>
      </div>
    </div>
  );
};

export const AuthCredentialsModal: React.FC<AuthCredentialsModalProps> = ({ 
  project,
  onClose,
}) => {
  const [credentials, setCredentials] = useState<CredentialParameter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [editingCredentialId, setEditingCredentialId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const { projectId } = useParams();

  useEffect(() => {
    fetchCredentials();
  }, [projectId]);

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/dev-agent/projects/${projectId}/credentials`);
      setCredentials(response.data.credentials || []);
    } catch (error: any) {
      console.error('Failed to fetch credentials:', error);
      setError('Failed to load credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleEdit = (credential: CredentialParameter) => {
    setEditingCredentialId(credential.id);
    // Prefill with existing value for plain_text type
    setEditValue(credential.value_type === 'plain_text' && credential.value ? credential.value : '');
    setUpdateError(null);
  };

  const handleCancelEdit = () => {
    setEditingCredentialId(null);
    setEditValue('');
    setUpdateError(null);
  };

  const handleUpdate = async (credentialId: string) => {
    try {
      setUpdating(true);
      setUpdateError(null);
      const response = await api.put(`/dev-agent/projects/${projectId}/credentials/${credentialId}`, {
        new_value: editValue || ''
      });

      if (response.data.success) {
        setEditingCredentialId(null);
        setEditValue('');
        await fetchCredentials();
      } else {
        setUpdateError('Failed to update credential');
      }
    } catch (error) {
      console.error('Failed to update credential:', error);
      setUpdateError('Failed to update credential');
    } finally {
      setUpdating(false);
    }
  };

  const renderCredentialItem = (credential: CredentialParameter, index: number) => {
    const isEditing = editingCredentialId === credential.id;
    
    return (
      <div
        key={credential.id}
        className="bg-bg-primary border border-border-secondary box-border flex flex-col gap-[8px] p-[var(--spacing-lg,12px)] rounded-[var(--radius-md,8px)] w-full group relative animate-fade-in-up opacity-0 transition-all duration-300"
        style={{
          boxShadow: "0px 1px 3px 0px var(--shadow-sm-01, rgba(10, 13, 18, 0.1)), 0px 1px 2px -1px var(--shadow-sm-02, rgba(10, 13, 18, 0.1))",
          animationDelay: `${index * 0.08}s`,
          animationFillMode: 'forwards',
          borderColor: isEditing ? 'var(--color-border-brand)' : undefined
        }}
      >
        {/* Main Content Row */}
        <div className="flex gap-[8px] items-start w-full">
          {/* Content */}
          <div className="flex flex-col gap-[6px] items-start flex-1 min-w-0">
            {/* Key name and badges */}
            <div className="flex gap-[11px] items-center w-full flex-wrap">
              <div className="bg-bg-primary border border-border-secondary box-border flex gap-[8px] items-center justify-center px-[8px] py-[var(--spacing-xs,4px)] rounded-[var(--radius-xs,4px)] shrink-0">
                <Text 
                  variant="text-xs" 
                  weight="medium" 
                  color="primary"
                  className="font-mono"
                >
                  {credential.key_name}
                </Text>
              </div>
              
              <div className="flex gap-[6px] items-center">
                {/* Type Badge */}
                <Pill>{credential.value_type === 'secure' ? 'Secure' : 'Text'}</Pill>

                {/* Status Badge */}
                <Pill variant={credential.has_value ? 'brand' : 'muted'}>
                  {credential.has_value ? 'Set' : 'Not Set'}
                </Pill>
              </div>
            </div>
            
            {/* Display name */}
            <Text 
              variant="text-sm" 
              weight="medium" 
              color="primary"
              className="w-full"
            >
              {credential.display_name}
            </Text>
            
            {/* Description */}
            <Text 
              variant="text-xs" 
              weight="regular" 
              color="tertiary"
              className="w-full break-words"
            >
              {credential.description}
            </Text>
            
            {/* Current Value - Only show for plain_text type when not editing */}
            {!isEditing && credential.value_type === 'plain_text' && credential.value && (
              <div className="w-full mt-[var(--spacing-xs,4px)]">
                <Text 
                  variant="text-xs" 
                  weight="medium" 
                  color="secondary"
                  className="mb-[2px]"
                >
                  Current Value:
                </Text>
                <div className="bg-bg-secondary px-[var(--spacing-md,8px)] py-[var(--spacing-xs,4px)] rounded-[var(--radius-sm,6px)] w-full">
                  <Text 
                    variant="text-xs" 
                    weight="regular" 
                    color="primary"
                    className="font-mono break-all"
                  >
                    {credential.value}
                  </Text>
                </div>
              </div>
            )}
          </div>

          {/* Edit Button */}
          {!isEditing && (
            <IfHasPermission
              permissions={project?.permissions}
              requires={Permissions.UpdateProjectPermission}
            >
              <button
                onClick={() => handleEdit(credential)}
                className="box-border flex gap-[8px] items-center justify-center px-[8px] py-[7px] rounded-[17px] shrink-0 w-[25px] h-[25px] hover:bg-bg-secondary-hover transition-colors"
                title="Edit credential"
              >
                <EditIcon className="text-text-tertiary hover:text-text-secondary transition-colors" />
              </button>
            </IfHasPermission>
          )}
        </div>

        {/* Inline Edit Section - Expands when editing */}
        {isEditing && (
          <div className="flex flex-col gap-[var(--spacing-md,8px)] pt-[var(--spacing-md,8px)] border-t border-border-secondary animate-fade-in-down">
            {/* Input Field */}
            <div>
              <Text variant="text-xs" weight="medium" color="secondary" className="mb-[var(--spacing-xs,4px)]">
                New Value
              </Text>
              <input
                type={credential.value_type === 'secure' ? 'password' : 'text'}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder="Enter new value"
                className="w-full px-[var(--spacing-lg,12px)] py-[var(--spacing-md,8px)] text-sm border border-border-secondary rounded-[var(--radius-md,8px)] focus:outline-none focus:border-border-primary-hover bg-bg-primary text-text-primary"
                style={{
                  boxShadow: "0px 1px 3px 0px var(--shadow-sm-01, rgba(10, 13, 18, 0.1)), 0px 1px 2px -1px var(--shadow-sm-02, rgba(10, 13, 18, 0.1))"
                }}
                autoFocus
              />
            </div>

            {/* Error Message */}
            {updateError && (
              <div className="flex items-center gap-[var(--spacing-xs,4px)] p-[var(--spacing-md,8px)] bg-bg-error-primary border border-border-error-subtle rounded-[var(--radius-sm,6px)]">
                <AlertCircle className="w-4 h-4 text-text-error-primary shrink-0" />
                <Text variant="text-xs" color="error">
                  {updateError}
                </Text>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-[var(--spacing-md,8px)]">
              <button
                onClick={handleCancelEdit}
                disabled={updating}
                className="px-[var(--spacing-lg,12px)] py-[var(--spacing-xs,4px)] text-sm font-medium text-text-secondary bg-bg-secondary border border-border-secondary rounded-[var(--radius-md,8px)] hover:bg-bg-secondary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdate(credential.id)}
                disabled={updating}
                className="px-[var(--spacing-lg,12px)] py-[var(--spacing-xs,4px)] text-sm font-medium text-text-primary-on-brand bg-bg-brand-solid rounded-[var(--radius-md,8px)] hover:bg-bg-brand-solid-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-[var(--spacing-xs,4px)]"
              >
                {updating && <Loader className="w-4 h-4 animate-spin" />}
                {updating ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[400px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Header */}
        <div className="bg-bg-primary box-border flex gap-[var(--spacing-md,8px)] items-start p-[var(--spacing-3xl,24px)] w-full">
          <div className="flex flex-[1_0_0] gap-[var(--spacing-xl,16px)] items-start min-h-px min-w-px">
            <div className="flex flex-[1_0_0] flex-col gap-[var(--spacing-xxs,2px)] items-start min-h-px min-w-px whitespace-pre-wrap">
              <Text variant="text-lg" weight="semibold" color="primary" className="w-full">
                Manage Credentials
              </Text>
              <Text variant="text-sm" weight="regular" color="secondary" className="w-full">
                Configure authentication and environment variables
              </Text>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center rounded-full w-8 h-8 text-fg-secondary hover:bg-bg-primary-hover transition-all duration-200 absolute right-[12px] top-[12px]"
          >
            <Icon name={IconName.Cross} size="md" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* Content */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-[var(--spacing-xl,16px)] w-full flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col gap-[var(--spacing-lg,12px)] w-full">
              {[0, 1, 2].map((index) => (
                <CredentialCardSkeleton key={index} index={index} />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-[var(--spacing-8xl,80px)] gap-[var(--spacing-lg,12px)]">
              <AlertCircle className="w-12 h-12 text-text-error-primary" />
              <Text variant="text-md" weight="semibold" color="primary">Error Loading Credentials</Text>
              <Text variant="text-sm" color="secondary" align="center">{error}</Text>
              <button
                onClick={fetchCredentials}
                className="mt-[var(--spacing-md,8px)] px-[var(--spacing-xl,16px)] py-[var(--spacing-md,8px)] bg-bg-brand-solid text-text-primary-on-brand rounded-[var(--radius-md,8px)] hover:bg-bg-brand-solid-hover transition-colors"
              >
                <Text variant="text-sm" weight="medium" color="on-brand">Try Again</Text>
              </button>
            </div>
          ) : credentials.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-[var(--spacing-8xl,80px)] gap-[var(--spacing-lg,12px)] text-center w-full">
              <Key className="w-12 h-12 text-text-quaternary" />
              <Text variant="text-md" weight="semibold" color="primary" align="center">No Credentials Found</Text>
              <Text variant="text-sm" color="secondary" align="center" className="max-w-[280px]">
                Credentials will appear here when the AI requests them during development.
              </Text>
            </div>
          ) : (
            <div className="flex flex-col gap-[var(--spacing-lg,12px)] w-full">
              {credentials.map((credential, index) => 
                renderCredentialItem(credential, index)
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCredentialsModal;

